import React, { useEffect, useState } from "react";
import CourierForm from "../../components/forms/CourierForm";
import Select from "../../components/common/Select";
import Cities from "./CitiesTable";
import { api } from "../../hooks/apiClient";
import { useStore } from "../../context/StoreContext";

const EMPTY_FORM = {
  id: "",
  store_id: "",
  courierName: "",
  courierApi: "",
  apiSecret: "",
  pickup_address_id: "",
  account: "",
};

/* ---------- small UI pieces ---------- */

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  const tone = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    error: "border-red-200 bg-red-50 text-red-800",
  }[toast.severity || "success"];

  return (
    <div className="fixed inset-x-0 top-4 z-50 flex justify-center px-4">
      <div
        role="status"
        className={`flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-md ${tone}`}
      >
        <span className="flex-1">{String(toast.children)}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="text-current opacity-60 hover:opacity-100"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? "bg-emerald-600" : "bg-slate-300"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const Badge = ({ tone = "slate", children }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const btnBase =
  "rounded-lg px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";
const btnPrimary = `${btnBase} bg-slate-900 text-white hover:bg-slate-800`;
const btnGhost = `${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;

/* ---------- page ---------- */

const Couriers = () => {
  const { selectedStore, stores } = useStore();
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couriersData, setCouriersData] = useState([]);
  const [cities, setCities] = useState();

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [courierOne, setOneCourier] = useState("");
  const [viewSet, setViewSet] = useState(true);
  const [snackbar, setSnackbar] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const closeToast = () => setSnackbar(null);
  const resetForm = () => setFormData(EMPTY_FORM);
  const findCourier = (id) => couriersData.find((c) => c.id === id);

  const fetchStoresCouriers = async (store_id) => {
    setLoading(true);
    try {
      const res = await api.get("couriers/all/bystore", {
        params: { store_id },
      });
      setCouriersData(res.data);
      if (res.data.length === 0) {
        setSnackbar({
          children: "No courier is registered with this store.",
          severity: "warning",
        });
      }
    } catch (err) {
      console.error("Error fetching couriers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (value) => {
    setSelectedStoreId(value);
    setCities(undefined);
    if (value) fetchStoresCouriers(value);
    else setCouriersData([]);
  };
  useEffect(() => {
    fetchStoresCouriers(selectedStore?.id);
  }, []);
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const viewCourier = () => {
    setViewSet((v) => !v);
    setIsEditMode(false);
    resetForm();
  };

  const handleEdit = (id) => {
    const courier = findCourier(id);
    if (!courier) return;
    setViewSet(false);
    setIsEditMode(true);
    setFormData({
      id,
      courierName: courier.name,
      courierApi: courier.api_key,
      apiSecret: courier.api_password,
      pickup_address_id: courier.pickup_address_id,
      account: courier.account,
    });
  };

  const syncCities = async (id, isActive) => {
    const courier = findCourier(id);
    if (!courier) return;
    setLoading(true);
    try {
      const res = await api.get("couriers/getcities", {
        params: {
          name: courier.name,
          name_id: courier.name_id,
          courier_id: courier.id,
          isActive,
        },
      });
      setSnackbar({ children: res.data, severity: "success" });
    } catch (err) {
      console.error("Error syncing cities:", err);
      setSnackbar({ children: "Could not sync cities", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (id) => {
    const courier = findCourier(id);
    if (!courier) return;
    setLoading(true);
    setOneCourier([courier]); // Cities component receives an array, same as before
    try {
      const res = await api.get("couriers/cities", {
        params: { courier_id: courier.id },
      });
      setCities(res.data.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = isEditMode
        ? await api.put(`couriers/update/${formData.id}`, formData)
        : await api.post("couriers/add", formData);
      setSnackbar({ children: res.data, severity: "success" });
      resetForm();
      setIsEditMode(false);
      setViewSet(true);
      if (selectedStoreId) fetchStoresCouriers(selectedStoreId);
    } catch (err) {
      console.error("Submit failed:", err);
      setSnackbar({ children: err.message, severity: "error" });
    }
  };

  const toggleActive = async (id, isActive) => {
    setLoading(true);
    try {
      const res = await api.put(`couriers/isactive/${id}`, {
        isActive: !isActive,
      });
      setSnackbar({ children: res.data, severity: "success" });
      await fetchStoresCouriers(selectedStoreId);
    } catch (err) {
      console.error("Toggle failed:", err);
      setSnackbar({ children: err.message, severity: "error" });
      setLoading(false);
    }
  };

  /* ---------- form view ---------- */
  if (!viewSet) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Toast toast={snackbar} onClose={closeToast} />
        <CourierForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          isEditMode={isEditMode}
          viewCourier={viewCourier}
          stores={stores}
        />
      </div>
    );
  }

  /* ---------- list view ---------- */
  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Toast toast={snackbar} onClose={closeToast} />

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Couriers</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Manage courier accounts and city coverage for each store.
          </p>
        </div>
        <button type="button" className={btnPrimary} onClick={viewCourier}>
          Add courier
        </button>
      </div>

      {/* Store filter */}
      <div className="mb-4 max-w-sm">
        <Select
          stores={stores ? stores : null}
          value={selectedStore?.id}
          onChange={handleSelectChange}
        />
      </div>

      {/* Loading bar */}
      <div className="mb-3 h-0.5 overflow-hidden rounded bg-slate-100">
        {loading && <div className="h-full w-1/3 animate-pulse bg-slate-900" />}
      </div>

      {/* List */}
      {!selectedStoreId ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">
          Select a store to see its couriers.
        </div>
      ) : couriersData.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">
          No couriers registered for this store yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {couriersData.map((courier) => (
            <li
              key={courier.id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                {/* Info */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-base font-semibold text-slate-900">
                      {courier.name}
                    </h2>
                    <Badge
                      tone={courier.status === "Approved" ? "green" : "slate"}
                    >
                      {courier.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    ID {courier.id} · Daily limit {courier.daily_limit}
                  </p>
                </div>

                {/* Status toggle */}
                <div className="flex items-center gap-3">
                  <span
                    className={`text-sm font-medium ${
                      courier.isActive ? "text-emerald-700" : "text-slate-500"
                    }`}
                  >
                    {courier.isActive ? "Active" : "Inactive"}
                  </span>
                  <Toggle
                    checked={!!courier.isActive}
                    disabled={loading}
                    label={`${courier.name} active`}
                    onChange={() => toggleActive(courier.id, courier.isActive)}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    className={btnPrimary}
                    disabled={loading}
                    onClick={() => fetchCities(courier.id)}
                  >
                    View cities
                  </button>
                  <button
                    className={btnGhost}
                    disabled={loading}
                    onClick={() => syncCities(courier.id, false)}
                  >
                    Sync cities
                  </button>
                  <button
                    className={btnGhost}
                    disabled={loading}
                    onClick={() => syncCities(courier.id, true)}
                  >
                    Sync active
                  </button>
                  <button
                    className={btnGhost}
                    onClick={() => handleEdit(courier.id)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Cities table */}
      {cities && (
        <div className="mt-6">
          <Cities courier={courierOne} cities={cities} iloading={loading} />
        </div>
      )}
    </div>
  );
};

export default Couriers;
// import React, { useEffect, useState } from "react";
// import Grid from "@mui/material/Grid";
// import Snackbar from "@mui/material/Snackbar";
// import Alert from "@mui/material/Alert";
// import CourierForm from "../../components/forms/CourierForm";
// import Switch from "@mui/material/Switch";
// import FormControlLabel from "@mui/material/FormControlLabel";
// import LinearProgress from "@mui/material/LinearProgress";
// import Select from "../../components/common/Select";
// import Cities from "./CitiesTable";
// import { api } from "../../hooks/apiClient";

// const Couriers = () => {
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [couriersData, setCouriersData] = useState([]);
//   const [cities, setCities] = useState();
//   const [stores, setStores] = useState([]);
//   const [selectedStoreId, setSelectedStoreId] = useState("");
//   const [courierOne, setOneCourier] = useState("");
//   const [viewSet, setViewSet] = useState(true);
//   const [snackbar, setSnackbar] = useState(null);
//   const handleCloseSnackbar = () => setSnackbar(null);
//   const [formData, setFormData] = useState({
//     id: "",
//     store_id: "",
//     courierName: "",
//     courierApi: "",
//     apiSecret: "",
//     pickup_address_id: "",
//     account: "",
//   });

//   const handleSelectChange = (value) => {
//     setSelectedStoreId(value);

//     if (value) {
//       fetchStoresCouriers(value);
//     } else {
//       setCouriersData([]);
//     }
//   };

//   const fetchStoresCouriers = async (store_id) => {
//     setLoading(true);
//     try {
//       const couriersResponse = await api.get("couriers/all/bystore", {
//         params: {
//           store_id: store_id,
//         },
//       });

//       setCouriersData(couriersResponse.data);

//       if (couriersResponse.data.length === 0) {
//         setSnackbar({
//           children: "No Courier is registerd with this store...",
//           severity: "success",
//         });
//       }
//     } catch (err) {
//       console.error("Error fetching stores and couriers:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     api
//       .get("stores/all")
//       .then((response) => {
//         setStores(response.data.data);
//         setLoading(false);
//       })
//       .catch((error) => {
//         setLoading(false);
//         console.error("Error fetching stores:", error);
//       });
//   }, []);

//   const resetForm = () => {
//     setFormData({
//       id: "",
//       store_id: "",
//       courierName: "",
//       courierApi: "",
//       apiSecret: "",
//       pickup_address_id: "",
//       account: "",
//     });
//   };
//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };
//   const viewCourier = () => {
//     setViewSet(!viewSet);
//     setIsEditMode(false);
//     resetForm();
//     if (!couriersData || couriersData.length === 0) {
//       setSnackbar({
//         children: "There is no store to show add  a new courier",
//         severity: "warning",
//       });
//     }
//   };

//   const handleEdit = async (id) => {
//     setViewSet((prevViewSet) => !prevViewSet);
//     setIsEditMode(true);
//     const courier = couriersData.filter((courier) => courier.id === id);
//     setFormData({
//       id: id,
//       courierName: courier[0].name,
//       courierApi: courier[0].api_key,
//       apiSecret: courier[0].api_password,
//       pickup_address_id: courier[0].pickup_address_id,
//       account: courier[0].account,
//     });
//   };

//   const getCitiesActive = async (id) => {
//     setLoading(true);
//     const courier = couriersData.filter((courier) => courier.id === id);
//     try {
//       const city = await api.get("couriers/getcities", {
//         params: {
//           name: courier[0].name,
//           name_id: courier[0].name_id,
//           courier_id: courier[0].id,
//           isActive: true,
//         },
//       });
//       setLoading(false);
//       setSnackbar({
//         children: city.data,
//         severity: "success",
//       });
//     } catch (err) {
//       console.error("Error fetching active cities:", err);
//     } finally {
//       setLoading(false);
//     }
//   };
//   const getCities = async (id) => {
//     setLoading(true);
//     const courier = couriersData.filter((courier) => courier.id === id);
//     try {
//       const city = await api.get("couriers/getcities", {
//         params: {
//           name: courier[0].name,
//           name_id: courier[0].name_id,
//           courier_id: courier[0].id,
//           isActive: false,
//         },
//       });
//       setLoading(false);
//       setSnackbar({
//         children: city.data,
//         severity: "success",
//       });
//     } catch (error) {
//       setLoading(false);
//       console.log(error);
//     }
//   };
//   const fetchCities = async (id) => {
//     setLoading(true);
//     const courier = couriersData.filter((courier) => courier.id === id);
//     setOneCourier(courier);
//     try {
//       const city = await api.get("couriers/cities", {
//         params: {
//           courier_id: courier[0].id,
//         },
//       });

//       setCities(city.data);
//       setLoading(false);
//     } catch (error) {
//       setLoading(false);
//       console.log(error);
//     }
//   };
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (isEditMode) {
//       try {
//         const response = await api.put(
//           `couriers/update/${formData.id}`,
//           formData,
//         );
//         setSnackbar({
//           children: response.data,
//           severity: "success",
//         });
//         setTimeout(() => {
//           resetForm();
//           window.location.reload();
//         }, 3000);
//       } catch (error) {
//         console.error("Submit Failed:", error);
//         setSnackbar({ children: error.message, severity: "error" });
//       }
//     } else {
//       try {
//         const response = await api.post(`couriers/add`, formData);
//         setSnackbar({
//           children: response.data,
//           severity: "success",
//         });
//         setTimeout(() => {
//           resetForm();
//           window.location.reload();
//         }, 3000);
//       } catch (error) {
//         console.error("Submit Failed:", error);
//         setSnackbar({ children: error.message, severity: "error" });
//       }
//     }
//   };
//   async function storeActive(id, cIsActive) {
//     setLoading(true);
//     try {
//       const response = await api.put(`couriers/isactive/${id}`, {
//         isActive: !cIsActive,
//       });

//       setTimeout(() => {
//         setSnackbar({
//           children: response.data,
//           severity: "success",
//         });
//         fetchStoresCouriers(selectedStoreId);
//       }, 1000);
//     } catch (error) {
//       console.error("Submit Failed:", error);
//       setSnackbar({ children: error.message, severity: "error" });
//       setLoading(false);
//     }
//   }
//   if (viewSet) {
//     return (
//       <div className="container mt-5">
//         {!!snackbar && (
//           <Snackbar
//             open
//             anchorOrigin={{ vertical: "top", horizontal: "center" }}
//             onClose={handleCloseSnackbar}
//             autoHideDuration={6000}
//           >
//             <Alert {...snackbar} onClose={handleCloseSnackbar} />
//           </Snackbar>
//         )}

//         <button
//           type="button"
//           className="btn btn-sm btn-outline-secondary float-end"
//           onClick={viewCourier}
//         >
//           <i className="bi bi-gear"></i> &nbsp; Add New Courier
//         </button>
//         <br />
//         <br />
//         <Grid item xs={12}>
//           <Grid item xs={8}>
//             <Select
//               stores={stores}
//               value={selectedStoreId}
//               onChange={handleSelectChange}
//             />
//             <br />
//             {loading && <LinearProgress />}
//             {couriersData.map((courier) => (
//               <div
//                 key={courier.id}
//                 className="card border-0 shadow-sm rounded-4 mb-3 courier-card"
//               >
//                 <div className="card-body p-1 p-md-3">
//                   <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
//                     {/* Left Section */}
//                     <div className="d-flex flex-column gap-2">
//                       <div className="d-flex align-items-center flex-wrap gap-2">
//                         <h5 className="mb-0 fw-bold text-dark">
//                           {courier.name}
//                         </h5>
//                         <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
//                           Limit: {courier.daily_limit}
//                         </span>
//                       </div>

//                       <div className="d-flex flex-wrap gap-2 text-muted small">
//                         <span className="badge bg-light text-dark border px-3 py-2">
//                           ID: {courier.id}
//                         </span>
//                         <span
//                           className={`badge px-3 py-2 ${
//                             courier.status === "Approved"
//                               ? "bg-success"
//                               : "bg-secondary"
//                           }`}
//                         >
//                           {courier.status}
//                         </span>
//                       </div>
//                     </div>

//                     {/* Middle Section */}
//                     <div className="d-flex align-items-center">
//                       <div className="d-flex align-items-center px-2 rounded-3 bg-light border">
//                         <FormControlLabel
//                           className="m-0"
//                           label={
//                             <span
//                               className={`fw-semibold ${
//                                 courier.isActive
//                                   ? "text-success"
//                                   : "text-danger"
//                               }`}
//                             >
//                               {courier.isActive ? "Active" : "Inactive"}
//                             </span>
//                           }
//                           control={
//                             <Switch
//                               checked={courier.isActive}
//                               onChange={() =>
//                                 storeActive(courier.id, courier.isActive)
//                               }
//                               inputProps={{ "aria-label": "controlled" }}
//                             />
//                           }
//                         />
//                       </div>
//                     </div>

//                     {/* Right Section */}
//                     <div className="d-flex flex-wrap gap-2 justify-content-xl-end">
//                       <button
//                         className="btn btn-sm btn-primary rounded-pill px-3"
//                         onClick={() => fetchCities(courier.id)}
//                       >
//                         <i className="bi bi-arrow-repeat me-1"></i>
//                         Fetch Cities
//                       </button>

//                       <button
//                         className="btn btn-sm btn-outline-primary rounded-pill px-3"
//                         onClick={() => getCities(courier.id)}
//                       >
//                         <i className="bi bi-buildings me-1"></i>
//                         Sync Cities
//                       </button>

//                       <button
//                         className="btn btn-sm btn-outline-success rounded-pill px-3"
//                         onClick={() => getCitiesActive(courier.id)}
//                       >
//                         <i className="bi bi-check2-circle me-1"></i>
//                         Sync Active Cities
//                       </button>

//                       <button
//                         className="btn btn-sm btn-outline-dark rounded-pill px-3"
//                         onClick={() => handleEdit(courier.id)}
//                       >
//                         <i className="bi bi-pencil-square me-1"></i>
//                         Edit
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//               // <div
//               //   key={courier.id}
//               //   className=" shadow p-1 mb-3 bg-body rounded"
//               // >
//               //   <div className=" d-flex justify-content-around">
//               //     <p className="magic-button magic-button-green ">
//               //       {courier.name} Limit-{courier.daily_limit}
//               //     </p>

//               //     <span>ID: {courier.id}</span>

//               //     <span>Status: {courier.status}</span>

//               //     <FormControlLabel
//               //       label={courier.isActive ? "Active " : "Inactive"}
//               //       control={
//               //         <Switch
//               //           checked={courier.isActive}
//               //           onChange={() =>
//               //             storeActive(courier.id, courier.isActive)
//               //           }
//               //           inputProps={{ "aria-label": "controlled" }}
//               //         />
//               //       }
//               //     />

//               //     <div className="">
//               //       <button
//               //         className="ms-3 btn btn-sm btn-outline-info"
//               //         onClick={() => fetchCities(courier.id)}
//               //       >
//               //         <i className="bi bi-pen"></i>&nbsp;Fetch Cities
//               //       </button>
//               //       <button
//               //         className="ms-3 btn btn-sm btn-outline-secondary"
//               //         onClick={() => getCities(courier.id)}
//               //       >
//               //         <i className="bi bi-buildings"></i>&nbsp;Sync Cities
//               //       </button>
//               //       <button
//               //         className="ms-3 btn btn-sm btn-outline-info"
//               //         onClick={() => getCitiesActive(courier.id)}
//               //       >
//               //         <i className="bi bi-buildings"></i>&nbsp;Sync Cities
//               //         Active
//               //       </button>
//               //       <button
//               //         className="ms-3 btn btn-sm btn-outline-secondary"
//               //         onClick={() => handleEdit(courier.id)}
//               //       >
//               //         <i className="bi bi-pen"></i>&nbsp;Edit
//               //       </button>
//               //     </div>
//               //   </div>
//               // </div>
//             ))}
//           </Grid>
//           {cities && (
//             <Grid xs={12}>
//               <Cities courier={courierOne} cities={cities} iloading={loading} />
//             </Grid>
//           )}
//         </Grid>
//       </div>
//     );
//   }

//   return (
//     <div className="container mt-5">
//       {!!snackbar && (
//         <Snackbar
//           open
//           anchorOrigin={{ vertical: "top", horizontal: "center" }}
//           onClose={handleCloseSnackbar}
//           autoHideDuration={6000}
//         >
//           <Alert {...snackbar} onClose={handleCloseSnackbar} />
//         </Snackbar>
//       )}

//       <CourierForm
//         formData={formData}
//         setFormData={setFormData}
//         handleChange={handleChange}
//         handleSubmit={handleSubmit}
//         isEditMode={isEditMode}
//         viewCourier={viewCourier}
//         stores={stores}
//       />
//     </div>
//   );
// };

// export default Couriers;
